import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { ErrorNotice } from '../components/ui/Notice';
import { Spinner } from '../components/ui/Skeleton';

/**
 * 🔴 Four states in, three outcomes — and the fourth is the one that matters.
 *
 * `unknown` (the session check itself failed) does NOT redirect to /login. Bouncing someone to a
 * login page because the API is down tells them the wrong thing: they sign in, it fails, and they
 * conclude their account is broken rather than that the service is. ⭐ "We could not ask" is not
 * "the answer was no".
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state, recheck } = useAuth();
  const location = useLocation();

  if (state.status === 'checking') {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <p className="flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]">
          <Spinner label="Checking session" /> Checking your session…
        </p>
      </div>
    );
  }

  if (state.status === 'unknown') {
    return (
      <div className="flex min-h-full items-start justify-center p-6">
        <div className="w-full max-w-md space-y-3">
          <ErrorNotice error={state.error} onRetry={recheck} />
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            You have not been signed out — PlanHub could not reach its API to check. Retry once it is
            back.
          </p>
        </div>
      </div>
    );
  }

  if (state.status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return <>{children}</>;
}
